import type { LectioAdjudicationCommand } from './readerCharacterIndexAdjudication'
import type { ChapterCharacterLayer, CharacterLayerNote, CharacterOccurrence } from '../types/characterLayerTypes'
import type { AnalyticalDimension, AnalyticalPosition, AnalyticalTaskSet } from '../types/analyticalTaskTypes'

export type ReaderCharacterAnalyticalDimension = Extract<
  AnalyticalDimension,
  'identity' | 'coreference' | 'characterization' | 'focalization' | 'speaker' | 'review'
>

export interface ReaderAnalyticalActivity {
  characterId: string
  dimension: ReaderCharacterAnalyticalDimension
  position: AnalyticalPosition
}

export interface ReaderAnalyticalMutation {
  activities: ReaderAnalyticalActivity[]
  merge?: {
    sourceCharacterId: string
    receivingCharacterId: string
  }
}

const occurrencePosition = (occurrence: CharacterOccurrence): AnalyticalPosition => ({
  rowId: occurrence.locator.rowId,
  ...(typeof occurrence.locator.localStart === 'number'
    ? { localOffset: occurrence.locator.localStart }
    : {}),
})

const notePosition = (note: CharacterLayerNote): AnalyticalPosition | null => {
  if (note.anchor.kind === 'span') {
    return {
      rowId: note.anchor.locator.rowId,
      ...(typeof note.anchor.locator.localStart === 'number'
        ? { localOffset: note.anchor.locator.localStart }
        : {}),
    }
  }
  if (note.anchor.kind === 'witness-row' || note.anchor.kind === 'collation-row') {
    return { rowId: note.anchor.rowId }
  }
  if (note.anchor.kind === 'row-selection' && note.anchor.rowIds[0]) {
    return { rowId: note.anchor.rowIds[0] }
  }
  return null
}

const occurrenceDimensions = (
  occurrence: CharacterOccurrence,
  forcedMentionType?: 'name' | 'coref',
): ReaderCharacterAnalyticalDimension[] => {
  const mentionType = forcedMentionType || occurrence.metadata?.mention_type || occurrence.metadata?.mentionType
  return mentionType === 'name' ? ['coreference', 'identity'] : ['coreference']
}

const noteDimension = (note: CharacterLayerNote): ReaderCharacterAnalyticalDimension | null => {
  const kind = note.metadata?.character_index_layer
  return kind === 'speaker' || kind === 'focalization' || kind === 'characterization'
    ? kind
    : null
}

const uniqueActivities = (activities: ReaderAnalyticalActivity[]): ReaderAnalyticalActivity[] => {
  const seen = new Set<string>()
  return activities.filter(activity => {
    const key = [activity.characterId, activity.dimension, activity.position.rowId, activity.position.localOffset ?? ''].join('|')
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * Translate one successful Reader adjudication into analytical activity.
 * This intentionally records work without inferring review or completion.
 */
export const resolveReaderAnalyticalMutation = (
  command: LectioAdjudicationCommand,
  layer: ChapterCharacterLayer,
): ReaderAnalyticalMutation => {
  if (command.op === 'restore') {
    return { activities: [] }
  }

  if (command.op === 'add') {
    const position = { rowId: command.rowId, localOffset: command.start }
    const dimensions: ReaderCharacterAnalyticalDimension[] = command.evidenceKind === 'name'
      ? ['coreference', 'identity']
      : command.evidenceKind === 'coref'
        ? ['coreference']
        : [command.evidenceKind]
    return {
      activities: dimensions.map(dimension => ({ characterId: command.characterId, dimension, position })),
    }
  }

  const occurrence = command.sourceKind === 'occurrence'
    ? layer.occurrences.find(candidate => candidate.occurrence_id === command.evidenceId)
    : undefined
  const note = command.sourceKind === 'layer'
    ? layer.notes.find(candidate => candidate.note_id === command.evidenceId)
    : undefined

  if (command.op === 'merge-character') {
    return {
      activities: occurrence ? [{
        characterId: command.characterId,
        dimension: 'coreference',
        position: occurrencePosition(occurrence),
      }] : [],
      merge: {
        sourceCharacterId: command.sourceCharacterId,
        receivingCharacterId: command.characterId,
      },
    }
  }

  if (occurrence) {
    const characterIds = command.op === 'toggle-shared-referent'
      ? [occurrence.character_id, command.characterId]
      : [command.op === 'claim' ? command.characterId : occurrence.character_id]
    const dimensions = occurrenceDimensions(
      occurrence,
      command.op === 'classify-occurrence' ? command.mentionType : undefined,
    )
    const position = occurrencePosition(occurrence)
    return {
      activities: uniqueActivities(characterIds.flatMap(characterId => (
        dimensions.map(dimension => ({ characterId, dimension, position }))
      ))),
    }
  }

  if (note) {
    const dimension = command.op === 'classify-layer' ? command.layerKind : noteDimension(note)
    const position = notePosition(note)
    const metadataCharacterId = typeof note.metadata?.character_id === 'string'
      ? note.metadata.character_id
      : note.anchor.kind === 'character'
        ? note.anchor.characterId
        : ''
    const characterId = command.op === 'claim' ? command.characterId : metadataCharacterId
    return dimension && position && characterId
      ? { activities: [{ characterId, dimension, position }] }
      : { activities: [] }
  }

  return { activities: [] }
}

/**
 * Discover evidence that predates the task tracker (for example a model run),
 * without replaying activity already represented in a workstream.
 */
export const collectUntrackedReaderAnalyticalActivities = (
  layer: ChapterCharacterLayer,
  taskSet: AnalyticalTaskSet,
  witnessId: string,
): ReaderAnalyticalActivity[] => {
  const needsTracking = (characterId: string, dimension: ReaderCharacterAnalyticalDimension): boolean => {
    const stream = taskSet.workstreams.find(candidate => (
      candidate.subjectId === `character:${characterId}` &&
      candidate.witnessId === witnessId &&
      candidate.dimension === dimension &&
      !candidate.archivedAt
    ))
    return !stream || (!stream.lastActivityAt && (stream.state === 'not_started' || stream.state === 'in_progress'))
  }
  const activities: ReaderAnalyticalActivity[] = []
  layer.characters.forEach(character => {
    const occurrences = layer.occurrences.filter(occurrence => (
      occurrence.character_id === character.character_id && occurrence.locator.witnessId === witnessId
    )).sort((left, right) => (
      left.locator.rowId.localeCompare(right.locator.rowId) ||
      (left.locator.localStart ?? 0) - (right.locator.localStart ?? 0)
    ))
    if (occurrences.length === 0) return
    const firstOccurrence = occurrences[0]
    if (needsTracking(character.character_id, 'coreference')) {
      activities.push({
        characterId: character.character_id,
        dimension: 'coreference',
        position: occurrencePosition(firstOccurrence),
      })
    }
    const firstName = occurrences.find(occurrence => (
      occurrence.metadata?.mention_type === 'name' || occurrence.metadata?.mentionType === 'name'
    ))
    if (firstName && needsTracking(character.character_id, 'identity')) {
      activities.push({
        characterId: character.character_id,
        dimension: 'identity',
        position: occurrencePosition(firstName),
      })
    }
    ;(['speaker', 'focalization', 'characterization'] as const).forEach(dimension => {
      if (!needsTracking(character.character_id, dimension)) return
      const note = layer.notes.find(candidate => (
        candidate.metadata?.character_id === character.character_id &&
        candidate.metadata?.character_index_layer === dimension &&
        candidate.anchor.kind === 'span' &&
        candidate.anchor.locator.witnessId === witnessId
      ))
      const position = note ? notePosition(note) : null
      if (position) activities.push({ characterId: character.character_id, dimension, position })
    })
  })
  return uniqueActivities(activities)
}
